import { useForm } from "@inertiajs/react";
import type { FormEvent } from "react";
import AuthOption from "@/components/forms/AuthOption";
import FieldError from "@/components/forms/FieldError";
import type { MonitoringEmailPreference } from "@/types";

type Props = {
  preference: MonitoringEmailPreference;
};

type FormData = {
  monitoring_email_preference: MonitoringEmailPreference;
};

const preferences: Array<{ label: string; value: MonitoringEmailPreference }> = [
  { label: "Always — send every weekly monitoring update", value: "always" },
  {
    label: "New and fixed issues — skip weeks with no opened or closed issues",
    value: "new_and_fixed",
  },
  { label: "New issues only — skip quiet weeks", value: "new_issues_only" },
  { label: "Never — skip the weekly monitoring update", value: "never" },
];

export default function MonitoringEmailPreferenceForm({ preference }: Props) {
  const { data, errors, patch, processing, setData, transform } = useForm<FormData>({
    monitoring_email_preference: preference,
  });

  transform(({ monitoring_email_preference }) => ({
    user: { monitoring_email_preference },
  }));

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    patch("/settings", { preserveScroll: true, preserveState: true });
  }

  return (
    <form className="card" onSubmit={handleSubmit}>
      <div className="card-body">
        <h2 className="text-lg font-semibold">Monitoring emails</h2>
        <p className="mt-1 text-sm text-gray-600">
          Choose when to receive the weekly monitoring update for your monitored pages and journeys.
        </p>

        <fieldset className="mt-5 space-y-3">
          <legend className="field-label">Send monitoring emails</legend>
          {preferences.map(({ label, value }) => (
            <AuthOption
              key={value}
              label={label}
              onSelect={() => setData("monitoring_email_preference", value)}
              selected={data.monitoring_email_preference === value}
            />
          ))}
          <FieldError error={errors.monitoring_email_preference} />
        </fieldset>

        <div className="mt-6 flex justify-end border-t border-gray-200 pt-6">
          <button type="submit" className="btn btn-primary" disabled={processing}>
            {processing ? "Saving…" : "Save preference"}
          </button>
        </div>
      </div>
    </form>
  );
}
